import { useNavigate, useParams, useSearchParams } from "react-router";

// Toolkit
import { assessmentUpdate } from "sj-ui-toolkit/src/client";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";
import { buildRoute } from "sj-ui-toolkit/src/constants/routes.js";
import { useTrackTime } from "sj-ui-toolkit/src/hooks/useTrackTime";
import { exitScormLesson } from "sj-ui-toolkit/src/utils/rxd";
import { useLearnContext } from "../../context/LearnContext";

// Hooks
import { useLessonLoader } from "../../hooks/useLessonLoader";

// Sub-components
import LessonCoverPage from "./LessonCoverPage.jsx";
import SandboxLesson from "./SandboxLesson";
import VideoLesson from "./VideoLesson";

// Main component
export default function LessonPage() {
  const { lessonKey } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isScorm, isGuest } = useLearnContext();

  // Use the lesson loader hook
  const { isLessonLoaded, lessonDetails, setLessonDetails } = useLessonLoader(lessonKey);
  const hasNextLesson =
    searchParams.get("inPath") === "true" && !!lessonDetails?.nextLesson?.lessonKey;

  // Track time spent on the lesson
  useTrackTime({ lessonId: lessonDetails.id });

  // Define handlers for user actions
  const submitKnowledgeRating = async (rating, isExiting = false) => {
    const isAfterPage = lessonDetails.showAfterSurvey;

    if (rating) {
      const params = {};
      if (isAfterPage) {
        params.after_rating = parseInt(rating, 10);
      } else {
        params.before_rating = parseInt(rating, 10);
      }

      try {
        await assessmentUpdate({
          id: lessonDetails.assessment.id,
          paramsToUpdate: params,
          useToast: false,
        });
      } catch (error) {
        console.error("Error submitting knowledge rating:", error);
      }
    }

    // Proceed based on whether it was before or after survey
    if (isAfterPage) {
      if (isExiting) {
        navigate("/");
        return;
      }
      // Navigate to the next lesson if we're in a path context and nextLesson exists
      if (hasNextLesson) {
        navigate(
          buildRoute(
            "Learn.routes.lesson.route",
            {
              lessonKey: lessonDetails.nextLesson?.lessonKey,
            },
            { inPath: true }
          )
        );
      } else {
        navigate("/");
      }
    } else {
      // Hide the before survey and start the lesson
      setLessonDetails({ ...lessonDetails, showBeforeSurvey: false });
    }
  };

  const onLessonFinish = () => {
    if (!isGuest && !isScorm) {
      setLessonDetails({ ...lessonDetails, showAfterSurvey: true });
    } else {
      exitScormLesson();
    }
  };

  // Return the full screen loader until we have lesson details
  if (!isLessonLoaded) {
    return (
      <div className="h-full">
        <FullScreenLoader />
      </div>
    );
  }

  // Render the After Lesson Feedback page if needed
  if (lessonDetails.showAfterSurvey) {
    return (
      <LessonCoverPage
        headerText="Lesson complete!"
        description="Let us know how you did."
        lessonType={lessonDetails.type}
        title={lessonDetails.title}
        level={lessonDetails.level}
        duration={lessonDetails.duration}
        isAfterLessonSurvey={true}
        onSubmit={submitKnowledgeRating}
        hasNextLesson={hasNextLesson}
      />
    );
  }

  // Render the Getting Started screen/survey if needed
  if (lessonDetails.showBeforeSurvey && !isGuest && !isScorm) {
    return (
      <LessonCoverPage
        headerText="Let's get started."
        description={lessonDetails.description}
        lessonType={lessonDetails.type}
        title={lessonDetails.title}
        level={lessonDetails.level}
        duration={lessonDetails.duration}
        isAfterLessonSurvey={false}
        onSubmit={submitKnowledgeRating}
      />
    );
  }

  // Render the appropriate lesson type
  if (lessonDetails.isBreakFix) {
    return <SandboxLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />;
  } else {
    return <VideoLesson lessonDetails={lessonDetails} onLessonFinish={onLessonFinish} />;
  }
}
